"use client";

import { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

export interface PremiumSlice {
  /** Sold & Approved only; `premium` is gross and includes Underwriting. */
  issuedPremium?: number;
  id: string;
  name: string;
  premium: number;
  // Carried from the tree node so the panels beneath the donut can show real
  // per-sub-agency detail instead of repeating the roll-up.
  policies?: number;
  agents?: number;
  averagePolicySize?: number;
  closingRatio?: number;
}

const COLORS = [
  "#2563eb", "#16a34a", "#f59e0b", "#db2777", "#7c3aed",
  "#0891b2", "#ea580c", "#4d7c0f", "#be123c", "#0d9488",
];

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;

/**
 * Inline premium-distribution breakdown — a pie + legend that opens smoothly
 * beneath an agency row (no popup). Shows each sub-agency's share of this
 * agency's total premium, rolled up across every level.
 */
export function PremiumPiePanel({
  agencyName,
  slices,
  ownPremium = 0,
  ownIssuedPremium = 0,
}: {
  agencyName: string;
  slices: PremiumSlice[];
  ownPremium?: number;
  ownIssuedPremium?: number;
}) {
  const { data, total, issuedTotal } = useMemo(() => {
    const rows = [
      ...(ownPremium > 0
        ? [{ id: "__own", name: `${agencyName} (direct)`, premium: ownPremium, issuedPremium: ownIssuedPremium }]
        : []),
      ...slices.filter((s) => s.premium > 0).sort((a, b) => b.premium - a.premium),
    ];
    const t = rows.reduce((sum, r) => sum + r.premium, 0);
    const it = rows.reduce((sum, r) => sum + (r.issuedPremium || 0), 0);
    return { data: rows, total: t, issuedTotal: it };
  }, [slices, ownPremium, ownIssuedPremium, agencyName]);

  const issuedShare = total > 0 ? (issuedTotal / total) * 100 : 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <p className="text-sm font-semibold text-gray-800">
          {agencyName}
          <span className="ml-1.5 font-normal text-muted-foreground">premium distribution</span>
        </p>
        <span className="shrink-0 text-[11px] uppercase tracking-wide text-muted-foreground">
          all levels
        </span>
      </div>

      {/* Reported vs issued as a single readable line, with a share bar. */}
      <div className="mb-4 flex items-end gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Total Production</p>
          <p className="text-xl font-bold tabular-nums tracking-tight text-gray-900">{money(total)}</p>
        </div>
        <div className="border-l pl-4">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Total Premium Sold</p>
          <p className="text-xl font-bold tabular-nums tracking-tight text-emerald-600">{money(issuedTotal)}</p>
        </div>
        <div className="ml-auto min-w-[110px] pb-1">
          <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>Sold share</span>
            <span className="font-semibold tabular-nums text-emerald-600">{issuedShare.toFixed(0)}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all"
              style={{ width: `${Math.min(100, issuedShare)}%` }}
            />
          </div>
        </div>
      </div>

      {total <= 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          No premium recorded in this period.
        </div>
      ) : (
        <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-start">
          <div className="relative h-[190px] w-[190px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="premium"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={58}
                  outerRadius={88}
                  paddingAngle={2}
                  stroke="none"
                >
                  {data.map((entry, i) => (
                    <Cell key={entry.id} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: unknown, _n: unknown, item: unknown) => {
                    const row = (item as { payload?: PremiumSlice })?.payload;
                    const pct = total > 0 ? Math.round((Number(value) / total) * 100) : 0;
                    return [
                      `${money(Number(value))} production (${pct}%) · ${money(row?.issuedPremium || 0)} sold`,
                      row?.name ?? "",
                    ];
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Centre label — the donut hole was dead space. */}
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Production</span>
              <span className="text-base font-bold tabular-nums tracking-tight text-gray-900">
                {money(total)}
              </span>
            </div>
          </div>

          <div className="pie-legend w-full flex-1">
            <div className="mb-1.5 flex items-center gap-2 border-b pb-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              <span className="w-2.5 shrink-0" />
              <span className="flex-1">Agency</span>
              <span className="w-24 text-right">Production</span>
              <span className="w-24 text-right">Premium sold</span>
              <span className="w-11 text-right">Share</span>
            </div>
            {data.map((row, i) => {
              const pct = total > 0 ? (row.premium / total) * 100 : 0;
              return (
                <div
                  key={row.id}
                  className="flex items-center gap-2 rounded py-1.5 text-xs transition-colors hover:bg-gray-50"
                >
                  <span
                    className="h-2.5 w-2.5 flex-shrink-0 rounded-sm"
                    style={{ backgroundColor: COLORS[i % COLORS.length] }}
                  />
                  <span className="flex-1 truncate text-gray-700">{row.name}</span>
                  <span className="w-24 text-right font-medium tabular-nums text-gray-900">
                    {money(row.premium)}
                  </span>
                  <span className="w-24 text-right font-medium tabular-nums text-emerald-600">
                    {money(row.issuedPremium || 0)}
                  </span>
                  <span className="w-11 text-right tabular-nums text-gray-500">
                    {pct.toFixed(1)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
